import React from "react";
import { Popover, Button, Typography, Box, Radio, RadioGroup, FormControlLabel, Divider } from "@mui/material";
import Toggle from "../ui/toggle";
import { ArrowDownAZ, ArrowUpAZ } from "lucide-react";
import DateRangeFilter from "../ui/date-range-filter";

export interface SimpleDateFilterParams {
    startDate?: string;
    endDate?: string;
    // null signals "this column owned the sort and it was cleared - reset
    // to the page's default sort"; undefined means "don't touch sort".
    sortBy?: string | null;
    sortDirection?: 'ASC' | 'DESC';
    // Only present when `extraToggle` was passed in - see below.
    extraToggleValue?: boolean;
}

export interface SimpleDateFilterProps {
    anchorEl: HTMLElement | null;
    open: boolean;
    onApply: (params: SimpleDateFilterParams) => void;
    onClose: () => void;
    currentSort?: {
        sortBy?: string;
        sortDirection?: 'ASC' | 'DESC';
    };
    currentStartDate?: string;
    currentEndDate?: string;
    // Lets this same popover be reused across multiple date columns on one
    // page (e.g. invoiceDate vs provisionDate) without colliding - matches
    // the sortByValue pattern already established by DescriptionFilter.
    fieldLabel?: string;
    sortByValue?: string;
    // Prepopulated start/end when the filter toggle is switched on with no
    // existing filter - should reflect the actual min/max of the loaded
    // list rather than an arbitrary +/-1 year window.
    defaultRangeStart?: Date;
    defaultRangeEnd?: Date;
    // Optional second, independent toggle rendered below the range filter -
    // e.g. Provisions' End Date column uses this for "Hide expired". Kept
    // generic (just a label + current value) rather than named after any
    // one column's use case, since this popover is shared across columns.
    extraToggle?: {
        label: string;
        currentValue: boolean;
    };
    // Renders the same sort/filter controls inline (no Popover/anchor) for
    // use inside a combined modal instead of an anchored popup.
    embedded?: boolean;
    // Hides this section's own Apply Now/Clear buttons - used when a parent
    // (the combined filter modal) drives Apply/Clear for every section at
    // once via the imperative handle below instead.
    hideActions?: boolean;
}

export type SimpleDateFilterHandle = {
    applyNow: () => void;
    clear: () => void;
};

const todayPlusYears = (years: number) => {
    const d = new Date();
    d.setFullYear(d.getFullYear() + years);
    return d;
};

// Lean sort + date-range popover, distinct from date-filter.tsx (which is
// specialized for the transactions page's View-By/relative-range UI) - this
// one is just "sort by this date column" + "optionally restrict to a date
// range", reusable across any plain date column.
const SimpleDateFilter = React.forwardRef<SimpleDateFilterHandle, SimpleDateFilterProps>(({
    anchorEl,
    open,
    onApply,
    onClose,
    currentSort,
    currentStartDate,
    currentEndDate,
    fieldLabel = 'Date',
    sortByValue = 'date',
    defaultRangeStart,
    defaultRangeEnd,
    extraToggle,
    embedded = false,
    hideActions = false,
}, ref) => {
    const [filterEnabled, setFilterEnabled] = React.useState<boolean>(!!(currentStartDate || currentEndDate));
    const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc' | ''>(
        currentSort?.sortBy === sortByValue ? (currentSort?.sortDirection === 'ASC' ? 'asc' : 'desc') : ''
    );
    const [rangeStart, setRangeStart] = React.useState<Date>(currentStartDate ? new Date(currentStartDate) : (defaultRangeStart || todayPlusYears(-1)));
    const [rangeEnd, setRangeEnd] = React.useState<Date>(currentEndDate ? new Date(currentEndDate) : (defaultRangeEnd || todayPlusYears(1)));
    const [extraToggleChecked, setExtraToggleChecked] = React.useState<boolean>(extraToggle?.currentValue ?? true);

    React.useEffect(() => {
        setFilterEnabled(!!(currentStartDate || currentEndDate));
        setSortOrder(currentSort?.sortBy === sortByValue ? (currentSort?.sortDirection === 'ASC' ? 'asc' : 'desc') : '');
        setRangeStart(currentStartDate ? new Date(currentStartDate) : (defaultRangeStart || todayPlusYears(-1)));
        setRangeEnd(currentEndDate ? new Date(currentEndDate) : (defaultRangeEnd || todayPlusYears(1)));
    }, [currentStartDate, currentEndDate, currentSort, sortByValue, defaultRangeStart, defaultRangeEnd]);

    React.useEffect(() => {
        setExtraToggleChecked(extraToggle?.currentValue ?? true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [extraToggle?.currentValue]);

    const buildApplyParams = (): SimpleDateFilterParams => {
        const params: SimpleDateFilterParams = {
            startDate: filterEnabled ? rangeStart.toISOString().slice(0, 10) : undefined,
            endDate: filterEnabled ? rangeEnd.toISOString().slice(0, 10) : undefined,
        };
        if (sortOrder) {
            params.sortBy = sortByValue;
            params.sortDirection = sortOrder.toUpperCase() as 'ASC' | 'DESC';
        }
        if (extraToggle) {
            params.extraToggleValue = extraToggleChecked;
        }
        return params;
    };

    const handleClear = () => {
        setFilterEnabled(false);
        setSortOrder('');
        setExtraToggleChecked(true);
        const params: SimpleDateFilterParams = { startDate: undefined, endDate: undefined };
        // Only signal a sort reset if this column currently owns the sort -
        // otherwise leave whatever sort is active on another column alone.
        if (currentSort?.sortBy === sortByValue) {
            params.sortBy = null;
        }
        if (extraToggle) {
            params.extraToggleValue = true;
        }
        onApply(params);
    };

    React.useImperativeHandle(ref, () => ({
        applyNow: () => onApply(buildApplyParams()),
        clear: handleClear,
    }));

    const content = (
        <>
            {!embedded && <>
                <Typography sx={{ fontWeight: 700, fontSize: 18 }}>Sort & Filter</Typography>
                <Divider sx={{ my: 1 }} />
            </>}
            <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Typography sx={{ fontSize: 14 }}>Sort By {fieldLabel}</Typography>
            </Box>
            <RadioGroup value={sortOrder || ''} onChange={e => setSortOrder(e.target.value as 'asc' | 'desc')} sx={{ mb: 1, pl: 3 }}>
                <FormControlLabel
                    value="desc"
                    sx={{ height: 24 }}
                    control={<Radio size='small' />}
                    label={<Typography sx={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 1 }}><ArrowUpAZ size={14} />Newest to oldest</Typography>}
                />
                <FormControlLabel
                    value="asc"
                    sx={{ height: 24 }}
                    control={<Radio size='small' />}
                    label={<Typography sx={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 1 }}><ArrowDownAZ size={14} />Oldest to newest</Typography>}
                />
            </RadioGroup>
            <Divider sx={{ mb: 1 }} />
            <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Toggle checked={filterEnabled} onChange={setFilterEnabled} />
                <Typography sx={{ fontSize: 14 }}>Filter By {fieldLabel} Range</Typography>
            </Box>
            {filterEnabled && (
                <Box sx={{ mb: 1 }}>
                    <DateRangeFilter
                        start={rangeStart}
                        end={rangeEnd}
                        separator="to"
                        sx={{ height: 30, borderRadius: 1 }}
                        onChange={(s, e) => { setRangeStart(s); setRangeEnd(e); }}
                    />
                </Box>
            )}
            {extraToggle && (
                <>
                    <Divider sx={{ mb: 1 }} />
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Toggle checked={extraToggleChecked} onChange={setExtraToggleChecked} />
                        <Typography sx={{ fontSize: 14 }}>{extraToggle.label}</Typography>
                    </Box>
                </>
            )}
            {!hideActions && (
                <Box display="flex" gap={1} mt={2}>
                    <Button
                        fullWidth
                        variant="contained"
                        sx={{ background: 'var(--color-primary)', color: '#fff', borderRadius: 2, fontWeight: 600, boxShadow: 'none', whiteSpace: 'nowrap', '&:hover': { background: 'var(--color-primary)' } }}
                        onClick={() => onApply(buildApplyParams())}
                    >
                        Apply Now
                    </Button>
                    <Button
                        fullWidth
                        variant="contained"
                        sx={{ background: 'var(--color-container-background)', color: 'var(--color-text-muted)', borderRadius: 2, fontWeight: 600, boxShadow: 'none', whiteSpace: 'nowrap', '&:hover': { background: 'var(--color-container-background)' } }}
                        onClick={handleClear}
                    >
                        Clear
                    </Button>
                </Box>
            )}
        </>
    );

    if (embedded) {
        return <Box>{content}</Box>;
    }

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            PaperProps={{ sx: { borderRadius: 2, boxShadow: 3, p: 2, minWidth: 320 } }}
        >
            {content}
        </Popover>
    );
});

export default SimpleDateFilter;
